import type { QueryFunctionContext } from '@tanstack/vue-query';
import type {
  CustomerAnalyticsErrorsResponse,
  CustomerDownloadEngagementInput,
  CustomerDownloadEngagementResponse,
  CustomerPortalImagesResponse,
  CustomerPortalSessionResponse,
  DigitalExtrasResponse,
} from '@/shared/types/api';
import {
  type ApiQuery,
  api,
  captureApiError,
  instrument,
  trackApiError,
  unwrap,
} from '../client';

/**
 * Customer download portal — the page a visitor opens on their own phone.
 *
 * There is no kiosk api_key there; the download code in the URL is the
 * credential and the backend answers every miss with 404, malformed or
 * expired alike, so the keyspace shape is never revealed. The code is
 * checked here too so a bogus one never burns a request.
 */

const CUSTOMER_DOWNLOAD_CODE_RE = /^[a-z0-9]{8}$/;

function assertValidDownloadCode(code: string): string {
  const normalized = code.toLowerCase();
  if (!CUSTOMER_DOWNLOAD_CODE_RE.test(normalized)) {
    throw new Error(`customer_download: invalid download_code ${code}`);
  }
  return normalized;
}

export function getPortalSession(
  downloadCode: string
): Promise<CustomerPortalSessionResponse> {
  const code = assertValidDownloadCode(downloadCode);
  return unwrap(
    api.GET('/v1/customer/sessions/{download_code}', {
      params: { path: { download_code: code } },
    })
  );
}

export function getPortalImages(
  downloadCode: string
): Promise<CustomerPortalImagesResponse> {
  const code = assertValidDownloadCode(downloadCode);
  return unwrap(
    api.GET('/v1/customer/sessions/{download_code}/images', {
      params: { path: { download_code: code } },
    })
  ).catch((error: Error) =>
    trackApiError(error, 'api_error_portraits_in_session')
  );
}

export function getDigitalExtras(
  downloadCode: string,
  signal: AbortSignal
): Promise<DigitalExtrasResponse> {
  const code = assertValidDownloadCode(downloadCode);
  return instrument(
    {
      span: 'api.get_digital_extras',
      errorTag: 'greenscreen_extras_list_extras',
      // useCustomerDigitalExtras files the failure under its own feature tags.
      skipSentryCapture: true,
    },
    () =>
      unwrap(
        api.GET('/v1/customer/sessions/{download_code}/extras', {
          params: { path: { download_code: code } },
          signal,
          silent: true,
        })
      )
  );
}

// The functions above read the code in lowercase: one code is one cache entry.
function portalKey(resource: string, downloadCode: string): [string, string] {
  return [resource, downloadCode.toLowerCase()];
}

// The code form reads the session, and then the code page reads it again: the
// page takes the answer of the form.
const PORTAL_SESSION_REUSE_MS = 60_000;

export function portalSessionQuery(
  downloadCode: string
): ApiQuery<CustomerPortalSessionResponse> {
  return {
    queryKey: portalKey('customer-session', downloadCode),
    queryFn: () => getPortalSession(downloadCode),
    staleTime: PORTAL_SESSION_REUSE_MS,
  };
}

export function portalImagesQuery(
  downloadCode: string
): ApiQuery<CustomerPortalImagesResponse> {
  return {
    queryKey: portalKey('customer-images', downloadCode),
    queryFn: () => getPortalImages(downloadCode),
  };
}

export function digitalExtrasQuery(
  downloadCode: string
): ApiQuery<DigitalExtrasResponse> {
  return {
    queryKey: portalKey('greenscreen-extras', downloadCode),
    queryFn: ({
      signal,
    }: QueryFunctionContext): Promise<DigitalExtrasResponse> =>
      getDigitalExtras(downloadCode, signal),
  };
}

function captureCustomerBeacon(
  error: Error,
  endpoint: string,
  extra: Record<string, unknown>
): never {
  captureApiError(error, {
    endpoint,
    extra,
    // 404 is a pasted expired code; a flaky phone network is not actionable.
    skipOnStatus: [404],
    skipNetworkFailures: true,
  });
  throw error;
}

export function postDownloadEngagement(
  downloadCode: string,
  fields: CustomerDownloadEngagementInput
): Promise<CustomerDownloadEngagementResponse> {
  const code = assertValidDownloadCode(downloadCode);
  return unwrap(
    api.POST('/v1/customer/sessions/{download_code}/engagement', {
      params: { path: { download_code: code } },
      body: fields,
    })
  ).catch((error: Error) =>
    captureCustomerBeacon(error, 'customer_record_engagement', {
      download_code_length: code.length,
      fields: Object.keys(fields),
    })
  );
}

export function postAnalyticsErrors(
  downloadCode: string,
  errorCodes: string[]
): Promise<CustomerAnalyticsErrorsResponse> {
  const code = assertValidDownloadCode(downloadCode);
  return unwrap(
    api.POST('/v1/customer/sessions/{download_code}/errors', {
      params: { path: { download_code: code } },
      body: { data: errorCodes },
    })
  ).catch((error: Error) =>
    captureCustomerBeacon(error, 'customer_record_errors', {
      download_code_length: code.length,
      error_count: errorCodes.length,
    })
  );
}
